import { Check, Circle, FileText, Loader2, OctagonAlert, PackageCheck, ShieldCheck } from "lucide-react";

/**
 * PHASE RAIL — the honest return of Draft -> Review -> Package, rebuilt for one screen.
 *
 * The operator's #1 confusion was "I don't know where I am or what's next." This is an always-visible
 * four-stop spine driven by one state machine (never a % bar — MEMORY: state by shape+text, no accent
 * rails). The QA stop shows a real BLOCKED branch, not only in-progress->done, because a run can fail.
 */
export type StudioPhase = "draft" | "qa-running" | "ready" | "blocked" | "done";

const STOPS: { id: StudioPhase | "qa"; label: string; hint: string }[] = [
  { id: "draft", label: "Draft edits", hint: "Decide each suggestion" },
  { id: "qa", label: "Approve & QA", hint: "Run the checks" },
  { id: "ready", label: "Ready", hint: "Cleared to export" },
  { id: "done", label: "Done", hint: "Downloaded or sent" }
];

/** Which stop is the operator AT, given the live phase. */
function activeIndex(phase: StudioPhase): number {
  if (phase === "draft") return 0;
  if (phase === "qa-running" || phase === "blocked") return 1;
  if (phase === "ready") return 2;
  return 3; // done
}

export function PhaseRail({ phase }: { phase: StudioPhase }) {
  const active = activeIndex(phase);
  return (
    <ol className="phase-rail" data-testid="phase-rail" aria-label="Where this run is">
      {STOPS.map((stop, index) => {
        // The "Approve & QA" stop carries the live QA sub-state (running / blocked).
        const isQaStop = stop.id === "qa";
        const state =
          index < active
            ? "past"
            : index > active
              ? "future"
              : isQaStop && phase === "qa-running"
                ? "running"
                : isQaStop && phase === "blocked"
                  ? "blocked"
                  : "current";
        // The Ready stop turns into a blocked marker when QA blocked.
        const label = stop.id === "ready" && phase === "blocked" ? "Blocked" : stop.label;
        return (
          <li key={stop.id} className="phase-stop" data-state={state}>
            <span className="phase-glyph" aria-hidden="true">
              {state === "past" ? (
                <Check />
              ) : state === "running" ? (
                <Loader2 className="spin" />
              ) : state === "blocked" ? (
                <OctagonAlert />
              ) : stop.id === "draft" ? (
                <FileText />
              ) : stop.id === "qa" ? (
                <ShieldCheck />
              ) : stop.id === "ready" ? (
                <PackageCheck />
              ) : (
                <Circle />
              )}
            </span>
            <span className="phase-copy">
              <span className="phase-label">{label}</span>
              <span className="phase-hint">{stop.hint}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
